/**
 * useIntent — read + persist signup intent for the public-shareable-link
 * flow (public-shareable-link spec R3.2).
 *
 * On mount:
 *  1. Read `?intent=` + `?source=` from `window.location.search`. If
 *     present, write to `sessionStorage[STORAGE_KEY]` for same-tab
 *     survival across in-tab navigations + the consent-gate detour.
 *  2. If no URL params, fall back to whatever sessionStorage already
 *     has — covers the post-refresh and consent-gate-detour cases.
 *  3. For `intent=remix` + valid source, fetch the source project's
 *     title via `GET /share/{source}/meta` so the IntentRibbon can
 *     show "Finishing your remix — `{title}` (your version) will be
 *     waiting".
 *
 * `clear()` removes the sessionStorage entry — called by the
 * post-completion router after successful intent resolution
 * (single-use; a future signup in the same browser doesn't accidentally
 * re-fire).
 *
 * The hook is intentionally side-effect-only at the URL/sessionStorage
 * boundary; it does NOT trigger the actual remix/navigation. That's
 * `resolveIntent`'s job and happens after SignupFlow's `onComplete`.
 */
import { useEffect, useState } from "react";
import { API_BASE } from "../api";

const STORAGE_KEY = "wbm:signup_intent";

/** Every kind a link may carry. `avatar`: a share visitor picked "You" in a project's avatar
 *  switch and signs up to see themselves in it (game-on-freeform U9). `save`: a share visitor
 *  saves the project (U10 carries it; Save's own unit makes it save). One list, so the URL,
 *  sessionStorage, App's routing and the ribbon cannot disagree about a kind. */
export const INTENT_KINDS = ["remix", "make_own", "avatar", "save"] as const;
export type IntentKind = (typeof INTENT_KINDS)[number];

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** The intent a raw (intent, source) pair names, or null. Every kind but `make_own` is about
 *  one project and needs its uuid; without one it is dropped, not half-honoured. `make_own`
 *  keeps a uuid source when it has one: the project it came from, saved if making does not
 *  happen yet (U16). */
export function toIntent(intent: unknown, source: unknown): PersistedIntent | null {
  if (!(INTENT_KINDS as readonly unknown[]).includes(intent)) return null;
  const kind = intent as IntentKind;
  if (kind === "make_own") return typeof source === "string" && UUID.test(source) ? { intent: kind, source } : { intent: kind };
  if (typeof source !== "string" || !UUID.test(source)) return null;
  return { intent: kind, source };
}

export interface PersistedIntent {
  intent: IntentKind;
  source?: string;
}

export interface IntentState {
  intent: IntentKind | null;
  source: string | null;
  /** Resolved via /share/{source}/meta when intent=remix. Null otherwise
   *  or while loading. */
  sourceTitle: string | null;
  /** Wipe sessionStorage — call after a successful intent resolution. */
  clear: () => void;
}

function readSessionIntent(): PersistedIntent | null {
  if (typeof sessionStorage === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { intent?: unknown; source?: unknown } | null;
    return parsed ? toIntent(parsed.intent, parsed.source) : null;
  } catch {
    return null;
  }
}

function writeSessionIntent(value: PersistedIntent): void {
  if (typeof sessionStorage === "undefined") return;
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    /* Storage may be full / disabled; degrade silently. */
  }
}

function clearSessionIntent(): void {
  if (typeof sessionStorage === "undefined") return;
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

/** Lower-level helpers exported so resolveIntent (which runs at the
 *  end of signup) can read the same persisted state without re-mounting
 *  a React hook. */
export function readPersistedIntent(): PersistedIntent | null {
  // Prefer URL param if present (handles direct-link case where the
  // hook hasn't mounted yet); otherwise sessionStorage.
  if (typeof window !== "undefined") {
    const params = new URLSearchParams(window.location.search);
    const fromUrl = toIntent(params.get("intent"), params.get("source"));
    if (fromUrl) return fromUrl;
  }
  return readSessionIntent();
}

export function clearPersistedIntent(): void {
  clearSessionIntent();
}

const NEXT_KEY = "wbm:signup_intent_next";

/** A sign-in's answer can carry `next_url`: the backend resolved an intent the account
 *  REGISTERED with (auth.py `_consume_pending_intent`, a first sign-in on any device). Kept
 *  for the signed-in shell, which prefers it to resolving its own copy, so one intent never
 *  makes two remixes. Only a same-site path is kept. */
export function noteLoginNextUrl(data: unknown): void {
  const next = (data as { next_url?: unknown } | null)?.next_url;
  if (typeof next !== "string" || !next.startsWith("/") || next.startsWith("//")) return;
  try {
    sessionStorage.setItem(NEXT_KEY, next);
  } catch {
    /* storage blocked: the shell resolves its own copy instead */
  }
}

/** The kept `next_url`, removed as it is read. */
export function takeLoginNextUrl(): string | null {
  try {
    const next = sessionStorage.getItem(NEXT_KEY);
    sessionStorage.removeItem(NEXT_KEY);
    return next;
  } catch {
    return null;
  }
}

export function useIntent(): IntentState {
  const [state, setState] = useState<{
    intent: IntentKind | null;
    source: string | null;
  }>(() => {
    const persisted = readPersistedIntent();
    return {
      intent: persisted?.intent ?? null,
      source: persisted?.source ?? null,
    };
  });
  const [sourceTitle, setSourceTitle] = useState<string | null>(null);

  // On mount, mirror URL params into sessionStorage so a refresh /
  // consent-gate detour keeps the intent. Done once per mount; subsequent
  // URL changes don't trigger another read (the URL is the entry point,
  // not the steady state).
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const fromUrl = toIntent(params.get("intent"), params.get("source"));
    if (fromUrl) writeSessionIntent(fromUrl);
  }, []);

  // Fetch source title for remix intent so the IntentRibbon can show
  // a personal "Finishing your remix — {title}" line. Failure is
  // silent; ribbon falls back to a generic copy.
  useEffect(() => {
    if ((state.intent !== "remix" && state.intent !== "save") || !state.source) {
      setSourceTitle(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const resp = await fetch(`${API_BASE}/share/${state.source}/meta`);
        if (!resp.ok) return;
        const meta = await resp.json();
        if (!cancelled && typeof meta?.title === "string") {
          setSourceTitle(meta.title);
        }
      } catch {
        /* Network error — ribbon shows generic copy. */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [state.intent, state.source]);

  return {
    intent: state.intent,
    source: state.source,
    sourceTitle,
    clear: () => {
      clearSessionIntent();
      setState({ intent: null, source: null });
      setSourceTitle(null);
    },
  };
}
